/**
 * mapDeliveryChoices — adapter for the cart/wishlist deliveryChoices API.
 *
 * The `/api/cart/deliveryChoices/:optionCode` response has a different shape
 * from checkDeliveryDetails (v2): it's an array of size entries, each holding a
 * flat `deliveryDetails[]` list. This adapter reshapes it into `RawSlaOption[]`
 * so it can flow through the same mapping/rendering pipeline as PDP — keeping
 * the shared `DeliverySLAList` component (and its view model) untouched.
 */
import { DeliveryChoiceEntry, RawSlaOption } from './types';
import { beautyCheck } from '../../../utils/StandardSizeUtils';

type Qualifier = { name?: string; qualifier?: string; value?: string };

/** Shape the size selector / SizeQtySelection consumes (same as cartSelector.getSizeOptions). */
export interface SizeOption {
  value: string;
  label: string;
  code: string;
  disabled: boolean;
  isSelected?: boolean;
  stockLevel: number;
  sizeFormat: string;
}

/** Size-variant qualifiers of a deliveryChoices entry (`FnlSizeVariant.selected`). */
const sizeQualifiersOf = (entry?: DeliveryChoiceEntry): Qualifier[] =>
  (entry?.baseOptions || []).find(option => option?.variantType === 'FnlSizeVariant')?.selected
    ?.variantOptionQualifiers || [];

const qualifierValue = (qualifiers: Qualifier[], key: string): string =>
  qualifiers.find(item => item?.qualifier === key)?.value || '';

/**
 * Stock for a size = availableQuantity of its pre-selected seller option; when
 * the payload marks none, the best (max) available across options is used, so a
 * size is only reported out of stock when every seller is out.
 */
const stockLevelOf = (entry?: DeliveryChoiceEntry): number => {
  const details = Array.isArray(entry?.deliveryDetails) ? entry.deliveryDetails : [];
  if (details.length === 0) return 0;
  const selected = details.find(detail => detail?.isSelected);
  if (selected) return Number(selected.availableQuantity) || 0;
  return details.reduce((max, detail) => Math.max(max, Number(detail?.availableQuantity) || 0), 0);
};

/**
 * Convert a deliveryChoices response into the raw SLA option array the
 * DeliverySLA pipeline understands.
 *
 * @param response      raw deliveryChoices array
 * @param selectedSize  the size value (e.g. "XS") to pick the matching entry;
 *                      falls back to the first serviceable entry, then the first.
 */
/**
 * The entry the cards are drawn from: the selected size when it is in the
 * payload, else the first orderable one, else the first at all.
 */
const entryForSize = (
  response?: DeliveryChoiceEntry[] | null,
  selectedSize?: string
): DeliveryChoiceEntry | undefined => {
  const entries = Array.isArray(response) ? response : [];
  if (entries.length === 0) return undefined;
  if (selectedSize) {
    // Try exact skuId match first (size state is skuId when multiseller is on).
    const bySkuId = entries.find(item => item?.skuId === selectedSize);
    if (bySkuId) return bySkuId;
    // Fall back to size label match; prefer isSelected+available entry.
    const sizeMatches = entries.filter(item => item?.size?.value === selectedSize);
    if (sizeMatches.length > 0) {
      return (
        sizeMatches.find(item => item?.isSelected && !item?.isDisabled) ||
        sizeMatches.find(item => !item?.isDisabled) ||
        sizeMatches[0]
      );
    }
  }
  return entries.find(item => !item?.isDisabled) || entries[0];
};

/**
 * Whether this size has sellers beyond the ones on the cards — the flag the
 * "Other sellers available" link is gated on. Absent means no, so a payload
 * without the field never offers a list that would come back empty.
 */
export const hasMoreSellersForSize = (
  response?: DeliveryChoiceEntry[] | null,
  selectedSize?: string
): boolean => Boolean(entryForSize(response, selectedSize)?.hasMoreSellers);

export const deliveryChoicesToSlaOptions = (
  response?: DeliveryChoiceEntry[] | null,
  selectedSize?: string
): RawSlaOption[] => {
  const entry = entryForSize(response, selectedSize);
  if (!entry) return [];

  const details = Array.isArray(entry?.deliveryDetails) ? entry.deliveryDetails : [];
  const serviceable = !entry?.isDisabled;

  return details.map((detail, index) => {
    const optionPriceType = detail?.selectedPriceType;
    return {
      priceType: optionPriceType,
      // Sole option is treated as selected even if API omits isSelected.
      isSelected: Boolean(detail?.isSelected) || details.length === 1,
      priceGroupId: `${optionPriceType || 'sla'}-${index}`,
      // B2C: the seller behind this option — shown on the card, and sent back
      // by id on the item update.
      sellerId: detail?.sellerId,
      sellerName: detail?.sellerName,
      defaultDisplay: detail?.defaultDisplay,
      // No priceType means the API is not calling this option best or fastest,
      // so it gets no badge. Without this, normalizePriceType collapses the
      // empty value to "best" and the row would claim BEST PRICE. An explicit
      // empty tagLabel is what suppresses the badge (see resolveBadgeLabel).
      ...(optionPriceType ? {} : { tagLabel: '' }),
      price: {
        // deliveryChoices keeps price flat and discountPercent as a sibling;
        // fold them into the priceData shape the mapper expects.
        priceData: { ...(detail?.price || {}), discountPercent: detail?.discountPercent },
        wasPriceData: detail?.wasPriceData
      },
      serviceabilityDetails: {
        serviceability: serviceable,
        productDetails: [
          {
            serviceability: serviceable,
            eddUpper: detail?.eddUpper,
            cutOffTime: detail?.cutoffTime,
            deliverySLA: detail?.deliverySLA,
            deliveryMethod: detail?.shippingAction,
            productCode: entry?.skuId
          }
        ]
      },
      stock: { stockLevel: detail?.availableQuantity }
    };
  });
};

/**
 * Can this size be ordered, per the deliveryChoices payload?
 *
 * Two ways a size comes back unavailable:
 *  - its entry carries `isDisabled: true`
 *  - the payload has no entry for it at all — the response only lists sizes some
 *    seller can serve, so a missing size has no seller and is equally unorderable
 *
 * An empty / not-yet-loaded payload means nothing is *known* to be disabled, so
 * it reports false and the caller's existing stock-based logic stands alone.
 *
 * @param sizeCode   sku code of the size (matched first — unique per size)
 * @param sizeLabel  size label e.g. "XL" (fallback for payloads without skuId)
 */
export const isSizeDeliveryDisabled = (
  response?: DeliveryChoiceEntry[] | null,
  sizeCode?: string,
  sizeLabel?: string
): boolean => {
  const entries = Array.isArray(response) ? response : [];
  if (entries.length === 0) return false;

  const entry =
    (sizeCode && entries.find(item => item?.skuId === sizeCode)) ||
    (sizeLabel && entries.find(item => item?.size?.value === sizeLabel)) ||
    undefined;

  if (!entry) return true;
  return Boolean(entry.isDisabled);
};

/**
 * Build the size list for the cart size modal straight out of deliveryChoices.
 *
 * With multiseller on, the sizeVariants (stockCheck) call is skipped — this
 * response already carries every size (`size.value` / `skuId`), its variant
 * qualifiers (`baseOptions`) and its per-seller `availableQuantity`, so size
 * label, format and stock all come from here.
 *
 * A size entry that is `isDisabled` or has no stock on any seller option is kept
 * on screen but not selectable, matching the sizeVariants-driven behaviour.
 */
export const deliveryChoicesToSizeOptions = (
  response?: DeliveryChoiceEntry[] | null
): SizeOption[] => {
  const entries = Array.isArray(response) ? response : [];

  return entries.reduce<SizeOption[]>((acc, entry) => {
    const variantOptionQualifiers = sizeQualifiersOf(entry);
    const { valueWithUnit: beauty } = beautyCheck(variantOptionQualifiers, true);
    const sizeName =
      beauty ||
      entry?.size?.value ||
      qualifierValue(variantOptionQualifiers, 'size') ||
      qualifierValue(variantOptionQualifiers, 'standardSize');
    if (!sizeName) return acc;
    const stockLevel = stockLevelOf(entry);
    const skuId = entry?.skuId || '';
    return [
      ...acc,
      {
        // Use skuId as value so each seller SKU is a unique tile identity,
        // allowing selectedSize to highlight only the matching entry even when
        // multiple SKUs share the same size label (e.g. multi-seller "FS").
        value: skuId || sizeName,
        label: sizeName,
        code: skuId,
        disabled: Boolean(entry?.isDisabled) || stockLevel === 0,
        isSelected: Boolean(entry?.isSelected),
        stockLevel,
        sizeFormat:
          qualifierValue(variantOptionQualifiers, 'sizeFormat') ||
          qualifierValue(variantOptionQualifiers, 'standardSizeFormat')
      }
    ];
  }, []);
};

export default deliveryChoicesToSlaOptions;
